import { ArrowRightIcon, Download, FileUp, PlusIcon, Trash2Icon, Upload } from 'lucide-react';
import {
  ButtonWizard,
  HeadingWizard,
  ConfirmationDialog,
  GoToPreviousClaimPageLink,
  GlobalLoader,
} from '../..';
import { CardWizard, SideSheet, SideSheetHeader } from '../../../layouts';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SearchOutlined } from '@ant-design/icons';
import { AnimatePresence } from 'framer-motion';
import {
  ITaxCodeForm,
  ITaxCodeTable,
} from '../../../pages/TravelRequest/constants/travelreq.interface';
import { DEFAULT_TAX_CODE_FORM_VALUES } from '../../../pages/TravelRequest/constants/travelreq.constants';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { useReactTable } from '@tanstack/react-table';
import {
  extractErrorMessageFromAPI,
  formatDayJsDate,
  mapToDropdownOptions,
  BusinessMappingFilterStorageKeys,
  getSavedFilters,
  saveFilters,
} from '../../../utils';
import {
  useCreateTaxCodeMutation,
  useDeleteTaxCodesMutation,
  useGetCurrenciesQuery,
  useGetTaxCodesQuery,
  useLazyDownloadSampleTaxCodeQuery,
  useUpdateTaxCodeMutation,
  useUploadTaxCodeMutation,
} from '../../../store/rtkQuery/travelReq';
import { useNotification } from '../../../contexts';
import CommonTravelTable from './CommonTravelTable';
import { Checkbox, Input, Modal, Select, Spin, Tooltip } from 'antd';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import TaxCodeForm from './TaxCodeForm';
import debounce from 'debounce';
import { useNavigate } from 'react-router-dom';
const columnHelper = createColumnHelper<ITaxCodeTable>();

export default function TaxCodeMapping() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const defaultFilters = { page: 1, pageSize: 25, search: '' };
  const [appliedFilters, setAppliedFilters] = useState(() =>
    getSavedFilters(BusinessMappingFilterStorageKeys.TAX_CODE, defaultFilters),
  );

  const [localSearch, setLocalSearch] = useState<string | undefined>(
    appliedFilters.search || undefined,
  );
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [taxCodeShow, setTaxCodeShow] =
    useState(false);

  const taxCodeForm = useForm<ITaxCodeForm>({
    defaultValues: DEFAULT_TAX_CODE_FORM_VALUES,
  });

  const [createTaxCode, createTaxCodeResponse] =
    useCreateTaxCodeMutation();

  const taxCodeResponse =
    useGetTaxCodesQuery(appliedFilters);

  const [taxCodeUpdate, taxCodeUpdateResponse] =
    useUpdateTaxCodeMutation();
  const [deleteTaxCode, deleteTaxCodeResponse] =
    useDeleteTaxCodesMutation();

  const [triggerDownload, { isFetching: isDownloadLoading }] = useLazyDownloadSampleTaxCodeQuery();
  const [uploadTaxCode, { isLoading: isUploadingCSV }] =
    useUploadTaxCodeMutation();
  const [total, setTotal] = useState(0);
  const [showCSV, setShowCSV] = useState(false);

  useEffect(() => {
    if (taxCodeResponse.data) {
      if (appliedFilters.page === 1) {
        setTotal(taxCodeResponse.data?.data?.count ?? 0);
      }
    }
  }, [taxCodeResponse.data]);

  useEffect(() => {
    saveFilters(BusinessMappingFilterStorageKeys.TAX_CODE, appliedFilters);
  }, [appliedFilters]);

  const onSubmit = async (data: ITaxCodeForm) => {
    try {
      const payload = cleanPayload({
        ...data,
      });

      if (Object.keys(payload).length === 0) {
        return showNotification(
          'warning',
          'Warning',
          'Please fill at least one field.',
        );
      }

      if (editIndex !== null) {
        if (!data.id) {
          return showNotification(
            'error',
            'Error',
            'ID is required for updating Tax Code Mapping.',
          );
        }

        await taxCodeUpdate({ id: data.id, ...payload }).unwrap();
        showNotification(
          'success',
          'Success',
          'Tax Code Mapping updated successfully.',
        );
      } else {
        await createTaxCode(payload).unwrap();
        showNotification(
          'success',
          'Success',
          'Tax Code Mapping created successfully.',
        );
      }

      // Reset after submission
      setTaxCodeShow(false);
      setEditIndex(null);
      taxCodeForm.reset(
        DEFAULT_TAX_CODE_FORM_VALUES,
      );
    } catch (err) {
      console.error(err);
      showNotification(
        'error',
        'Error',
        'Failed to save Tax Code Mapping. Please try again.',
      );
    }
  };

  const onEdit = (index: number) => {
    const entry = table.getRowModel().rows[index]?.original;
    if (!entry) return;

    taxCodeForm.reset(entry);
    setEditIndex(index);
    setTaxCodeShow(true);
  };

  const cleanPayload = (payload: any) => {
    return Object.fromEntries(
      Object.entries(payload).filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
  };

  const onDeleteConfirm = async () => {
    try {
      const idsToDelete = Object.keys(selectedRows).filter(
        id => selectedRows[id],
      );
      if (idsToDelete.length > 0) {
        await deleteTaxCode(idsToDelete.map(Number)).unwrap();
        showNotification(
          'success',
          'Success',
          'Selected entries deleted successfully.',
        );
        setSelectedRows({});
        setAppliedFilters(prev => ({
          ...prev,
          page: 1,
        }));
        taxCodeResponse.refetch();
      }
    } catch (error) {
      console.error('Delete Error:', error);
      showNotification('error', 'Error', 'Failed to delete selected entries.');
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  const data = taxCodeResponse.data?.data?.list || [];

  const allSelected = useMemo(() => {
    return data.length > 0 && data.every((row: any) => selectedRows[row.id]);
  }, [data, selectedRows]);

  const someSelected = useMemo(() => {
    return data.some((row: any) => selectedRows[row.id]);
  }, [data, selectedRows]);

  const [selectedFile, setSelectedFile] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const blob: any = await triggerDownload(null).unwrap(); // no params needed
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tax_code_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleUploadExcel = async () => {

    if (!selectedFile) {
      showNotification("error", "Error", "Please select a file first");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile); // 'file' is your backend field name

      const response = await uploadTaxCode(formData).unwrap();
      showNotification(
        'success',
        'Success',
        response?.message || 'Request uploaded successfully',
      );
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) ||
        'Something went wrong . Please try again',
      );
    } finally {
      setSelectedFile(null);
      setShowCSV(false);
    }
  };

  const handleSelectAll = () => {
    const newSelection = allSelected
      ? {}
      : Object.fromEntries(data.map((row: any) => [row.id, true]));
    setSelectedRows(newSelection);
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: () => (
          <Checkbox
            indeterminate={someSelected}
            checked={allSelected}
            onChange={handleSelectAll}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={!!selectedRows[row.original.id]}
            onChange={() => handleSelectRow(row.original.id)}
          />
        ),
        size: 50,
      }),
      // Name + Description
      columnHelper.accessor('name', {
        header: () => 'Name',
        cell: ({ row }) => {
          const data = row.original;
          return `${data?.name}${data?.description ? ` - ${data.description}` : ''
            }`;
        },
      }),
      columnHelper.accessor('erpCode', {
        header: () => 'ERP Code',
        cell: ({ row }) => row.original.erpCode || '-',
      }),
      columnHelper.accessor('cgst', {
        header: () => 'CGST (%)',
        cell: ({ row }) => row.original.cgst ?? '0.0000',
      }),

      columnHelper.accessor('sgst', {
        header: () => 'SGST (%)',
        cell: ({ row }) => row.original.sgst ?? '0.0000',
      }),

      columnHelper.accessor('igst', {
        header: () => 'IGST (%)',
        cell: ({ row }) => row.original.igst ?? '0.0000',
      }),

      columnHelper.accessor('ugst', {
        header: () => 'UGST (%)',
        cell: ({ row }) => row.original.ugst ?? '0.0000',
      }),
      // HSN Code
      columnHelper.accessor('HSNCode', {
        header: () => 'HSN Code',
        cell: ({ row }) => row.original.HSNCode || '-',
      }),

      // Tax %
      columnHelper.accessor('taxPercentage', {
        header: () => 'Tax (%)',
        cell: ({ row }) => row.original.taxPercentage ?? '-',
      }),

      // Valid From
      columnHelper.accessor('validFrom', {
        header: () => 'Valid From',
        cell: info => (
          <span>
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
              : '-'}
          </span>
        ),
      }),

      // Valid To
      columnHelper.accessor('validTo', {
        header: () => 'Valid To',
        cell: info => (
          <span>
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
              : '-'}
          </span>
        ),
      }),

      // Country
      columnHelper.accessor('countryId', {
        header: () => 'Country',
        cell: ({ row }) => {
          const item = row.original.country;
          return item
            ? [item.Code, item.ShortDescription].filter(Boolean).join(' - ')
            : '-';
        },
      }),

      // State
      columnHelper.accessor('stateId', {
        header: () => 'State',
        cell: ({ row }) => {
          const item = row.original.state;
          return item
            ? [item.ShortDescription, item.StateName].filter(Boolean).join(' - ')
            : '-';
        },
      }),

      // Distribution Channel
      columnHelper.accessor('distributionChannelId', {
        header: () => 'Distribution Channel',
        cell: ({ row }) => {
          const item = row.original.distributionChannel;
          return item
            ? `${item.erpCode} - ${item.name || item.code}`
            : '-';
        },
      }),

      columnHelper.accessor('createdAt', {
        cell: info => (
          <span>
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
              : '-'}
          </span>
        ),
        header: () => <span>Created At</span>,
      }),

      // ✅ Created By
      columnHelper.accessor('createdBy', {
        header: () => 'Created By',
        cell: ({ row }) => row.original.createdByUser?.FullName || '-',
      }),

      columnHelper.accessor('updatedAt', {
        cell: info => (
          <span>
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
              : '-'}
          </span>
        ),
        header: () => <span>Last Updated</span>,
      }),

      columnHelper.accessor('updatedBy', {
        header: () => 'Updated By',
        cell: ({ row }) => row.original.updatedByUser?.FullName || '-',
      }),

      columnHelper.accessor('action', {
        header: () => '',
        cell: ({ row }) => (
          <div className="flex gap-1 justify-center">
            <PencilSquareIcon
              onClick={() => onEdit(row.index)}
              className="cursor-pointer size-3.5 stroke-[1px] text-accent-400"
            />
          </div>
        ),
        size: 10,
      }),
    ],
    [selectedRows, allSelected, someSelected],
  );

  useEffect(() => {
    const debouncedUpdate = debounce((value: string) => {
      setAppliedFilters(prev => ({ ...prev, search: value, page: 1 }));
    }, 1000);
    debouncedUpdate(localSearch);
    return () => {
      debouncedUpdate.clear();
    };
  }, [localSearch]);

  const table = useReactTable<ITaxCodeTable>({
    enableColumnPinning: true,
    columns,
    initialState: {
      columnPinning: {
        left: ['select', 'action'],
      },
    },
    data: taxCodeResponse.data?.data?.list || [],
    getCoreRowModel: getCoreRowModel(),
  });

  const mainContent = (
    <>
      {someSelected ? (
        <div className="flex gap-2 pl-4 mb-2">
          <>
            <ButtonWizard
              addOnClass="border border-error-500 mr-auto"
              addOnTextClass="text-error-500"
              buttonText="Delete"
              onBtnClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2Icon className="size-4 stroke-[3px] text-error-500 w-4 h-4" />
            </ButtonWizard>
          </>
        </div>
      ) : (
        <></>
      )}
      <CommonTravelTable
        table={table}
        total={total}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
      />
    </>
  );

  return (
    <div className="p-1.5 flex flex-col gap-2">
      <Spin spinning={deleteTaxCodeResponse.isLoading}>
        <div>
          <CardWizard addOnClass="p-0 pt-2.5">
            <div>
              <div className="flex items-center justify-between px-4 mb-3">
                <HeadingWizard addOnClass="text-[#6E6893] font-extrabold uppercase tracking-wider text-sm flex items-center gap-x-2">
                  <span>Tax Code Mapping</span>
                </HeadingWizard>
                <div className="flex items-center justify-end gap-2">
                  <Input
                    name="search"
                    value={localSearch}
                    allowClear
                    type="text"
                    onChange={e => setLocalSearch(e.target.value)}
                    placeholder="Search..."
                    className="w-48"
                    style={{
                      width: '200px',
                      backgroundColor: '#F2F0F9',
                      border: '1px solid #E5E7EB',
                    }}
                    prefix={
                      <SearchOutlined
                        style={{
                          color: '#6E6893',
                          fontSize: '16px',
                          marginRight: '2px',
                        }}
                      />
                    }
                  />


                  <ButtonWizard
                    addOnClass="flex items-center gap-1 rounded-md border border-primary-600 text-primary-600 transition px-3 py-1.5 text-sm font-medium"
                    addOnTextClass="text-primary-600"
                    onBtnClick={() => setShowCSV(true)}
                  >
                    <Tooltip title="Download & Upload Excel" placement="top">
                      <FileUp className="w-4 h-4" />
                    </Tooltip>
                  </ButtonWizard>
                  <ButtonWizard
                    addOnClass="bg-primary-600 h-8 px-4 flex items-center justify-center mb-1"
                    addOnTextClass={''}
                    buttonText="Add"
                    onBtnClick={() => {
                      setTaxCodeShow(true);
                      setEditIndex(null);
                      taxCodeForm.reset(
                        DEFAULT_TAX_CODE_FORM_VALUES,
                      );
                    }}
                  >
                    <PlusIcon className="size-4 stroke-[3px] font-semibold text-white" />
                  </ButtonWizard>
                  <span
                    onClick={() => navigate(-1)}
                    className={
                      'cursor-pointer text-xs px-2 py-2 rounded-md text-[#4649EC] font-semibold hover:opacity-80 hover:underline transition-colors flex items-center gap-1 w-max border border-[#4649EC]'
                    }
                  >
                    <span>Back</span>
                    <ArrowRightIcon className="size-3 stroke-[4px] text-[#4649EC]" />
                  </span>
                </div>
              </div>
            </div>
            {mainContent}
          </CardWizard>
        </div>
      </Spin>
      <AnimatePresence>
        {taxCodeShow && (
          <SideSheet
            isOpen={taxCodeShow}
            addOnClass="w-[50vw] sm:w-[30vw] max-w-unset"
            onClose={() => setTaxCodeShow(false)}
            contentChildren={
              <div className="p-4">
                <TaxCodeForm
                  form={taxCodeForm}
                />
              </div>
            }
            headerChildren={
              <div className="border-b border-gray-100">
                <SideSheetHeader
                  title={
                    editIndex !== null
                      ? 'Update Tax Code Mapping'
                      : 'Add New Tax Code Mapping'
                  }
                  onCloseClick={() => {
                    if (
                      !createTaxCodeResponse.isLoading ||
                      !taxCodeUpdateResponse.isLoading
                    ) {
                      setTaxCodeShow(false);
                    }
                  }}
                />
              </div>
            }
            footerChildren={
              <div className="p-4 border-t">
                <div className="border-gray-100 flex items-center gap-4">
                  <ButtonWizard
                    addOnClass="bg-white border border-primary-600"
                    addOnTextClass={'text-primary-600'}
                    buttonText="Cancel"
                    onBtnClick={() => {
                      taxCodeForm.reset(
                        DEFAULT_TAX_CODE_FORM_VALUES,
                      );
                      setTaxCodeShow(false);
                    }}
                    disabled={
                      createTaxCodeResponse.isLoading ||
                      taxCodeResponse.isLoading
                    }
                  ></ButtonWizard>
                  <ButtonWizard
                    addOnClass={'bg-primary-600'}
                    buttonText={editIndex !== null ? 'Update' : 'Save'}
                    onBtnClick={taxCodeForm.handleSubmit(
                      onSubmit,
                    )}
                    loading={
                      createTaxCodeResponse.isLoading ||
                      taxCodeUpdateResponse.isLoading
                    }
                  ></ButtonWizard>
                </div>
              </div>
            }
          />
        )}
      </AnimatePresence>
      <ConfirmationDialog
        loading={false}
        title="Are you sure?"
        onConfirm={onDeleteConfirm}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isOpen={isDeleteDialogOpen}
      >
        <p className="text-text-200 text-xs">
          Are you sure you want to delete selected entries?
        </p>
      </ConfirmationDialog>
      <GlobalLoader loading={deleteTaxCodeResponse.isLoading} />

      {/* request update detail by excel Modals */}
      <Modal
        open={showCSV}
        onCancel={() => setShowCSV(false)}
        footer={null}
        width={640}
        centered
        title="Excel Configuration"
      >
        <Spin spinning={isDownloadLoading || isUploadingCSV}>
          <div className="mt-4 space-y-6">

            <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-xs text-blue-700">
              Please download the template first and ensure the format remains unchanged before uploading.
            </div>
            {/* Simple Download Button */}
            <div>
              <button
                onClick={handleDownloadTemplate}
                className="w-full flex items-center justify-center gap-2
                   border border-gray-400 text-gray-800
                   hover:bg-gray-50 hover:border-gray-500
                   px-4 py-2 rounded-md text-sm font-medium
                   transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Excel
              </button>
            </div>

            {/* Full Width Upload Section */}
            <div className="space-y-4">

              {/* Dotted Upload Box */}
              <label className="w-full flex flex-col items-center justify-center 
                                border-2 border-dashed border-gray-300 
                                rounded-md px-4 py-8 cursor-pointer 
                                hover:border-gray-400 transition-colors text-center">
                <span className="text-sm text-gray-700">
                  Click to select file
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  Supported formats: .xlsx, .xls
                </span>

                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="hidden"
                />
              </label>

              {/* Selected File */}
              {selectedFile && (
                <div className="text-xs text-gray-600 truncate">
                  Selected File: {selectedFile.name}
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUploadExcel}
                disabled={!selectedFile}
                className="flex items-center justify-center gap-2 w-full
                   bg-gray-800 hover:bg-gray-900
                   disabled:bg-gray-300 disabled:cursor-not-allowed
                   px-4 py-2 rounded-md text-sm font-medium text-white"
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>

            </div>

          </div>
        </Spin>
      </Modal>
    </div>
  );
}
